/** @file Dopesheet key hit test: row by division, key by binary search on the row's sorted frames. */
import { readNum } from './_internal/readNum';
import { sortedLowerBound } from './_internal/sortedLowerBound';
import { HANDLE_PX } from './handlePx';
import type { TimelineLayout } from './timelineLayout';
import { timelineScale } from './timelineScale';
import type { TimelineScale } from './timelineScale';

/** A key under the pointer: row index, key index within the row's frames, and its frame. */
export interface TimelineKeyHit {
  row: number;
  key: number;
  frame: number;
}

/** Index of the frame nearest to f given its lower bound i, or -1 for an empty row. */
function nearest(frames: Float64Array, i: number, f: number): number {
  if (frames.length === 0) return -1;
  if (i === 0) return 0;
  if (i === frames.length) return i - 1;
  return f - readNum(frames, i - 1) <= readNum(frames, i) - f ? i - 1 : i;
}

/** Key at content point (x, y) within tolerancePx horizontally, or null; O(1) row + O(log n) key. */
export function hitTimelineKey(layout: TimelineLayout, scale: TimelineScale, x: number, y: number, tolerancePx: number = HANDLE_PX.key): TimelineKeyHit | null {
  const row = Math.floor(y / layout.rowHeight);
  const r = layout.rows[row];
  if (r === undefined) return null;
  const f = timelineScale.xToFrame(scale, x);
  const key = nearest(r.frames, sortedLowerBound(r.frames, f), f);
  if (key < 0) return null;
  const frame = readNum(r.frames, key);
  return Math.abs(timelineScale.frameToX(scale, frame) - x) <= tolerancePx ? { row, key, frame } : null;
}
